import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  type Auth,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  getMultiFactorResolver,
  type MultiFactorError,
  type MultiFactorResolver,
  multiFactor,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  type TotpSecret,
  type User,
} from "firebase/auth";
import { firebaseAuthEmulatorUrl, firebaseConfigurationReady, isDemoMode } from "../config/runtime";
import { demoAdminLoginToken } from "./admin-login-service";

export interface TotpEnrollment {
  qrCodeUrl: string;
  secretKey: string;
}

export interface SignInOutcome {
  mfaRequired: boolean;
}

export interface IdentityAdapter {
  beginTotpEnrollment(): Promise<TotpEnrollment>;
  completeTotpEnrollment(code: string): Promise<void>;
  currentToken(forceRefresh?: boolean): Promise<string | null>;
  signIn(email: string, password: string): Promise<SignInOutcome>;
  signInWithCustomToken(token: string): Promise<void>;
  signOut(): Promise<void>;
  verifyTotp(code: string): Promise<void>;
}

class DemoIdentityAdapter implements IdentityAdapter {
  private authenticated = false;

  async beginTotpEnrollment(): Promise<TotpEnrollment> {
    return {
      qrCodeUrl: "otpauth://totp/Cyra:admin@example.test?secret=JBSWY3DPEHPK3PXP&issuer=Cyra",
      secretKey: "JBSW Y3DP EHPK 3PXP",
    };
  }

  async completeTotpEnrollment(code: string): Promise<void> {
    await this.verifyCode(code);
  }

  async currentToken(): Promise<string | null> {
    return this.authenticated ? "demo-firebase-token" : null;
  }

  async signIn(email: string, password: string): Promise<SignInOutcome> {
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    if (!email.includes("@") || password.length < 1)
      throw new Error("The email or password does not match.");
    this.authenticated = true;
    return { mfaRequired: false };
  }

  async signInWithCustomToken(token: string): Promise<void> {
    if (token !== demoAdminLoginToken) throw new Error("The sign-in token is invalid or expired.");
    this.authenticated = true;
  }

  async signOut(): Promise<void> {
    this.authenticated = false;
  }

  async verifyTotp(code: string): Promise<void> {
    await this.verifyCode(code);
  }

  private async verifyCode(code: string): Promise<void> {
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    if (code !== "123456") throw new Error("The authenticator code is incorrect or expired.");
  }
}

class MissingFirebaseConfigurationAdapter implements IdentityAdapter {
  private fail(): never {
    throw new Error(
      "Firebase is not configured. Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, and VITE_FIREBASE_APP_ID before using API mode.",
    );
  }

  async beginTotpEnrollment(): Promise<TotpEnrollment> {
    return this.fail();
  }

  async completeTotpEnrollment(): Promise<void> {
    return this.fail();
  }

  async currentToken(): Promise<string | null> {
    return this.fail();
  }

  async signIn(): Promise<SignInOutcome> {
    return this.fail();
  }

  async signInWithCustomToken(): Promise<void> {
    return this.fail();
  }

  async signOut(): Promise<void> {
    return this.fail();
  }

  async verifyTotp(): Promise<void> {
    return this.fail();
  }
}

class FirebaseIdentityAdapter implements IdentityAdapter {
  private readonly auth: Auth;
  private readonly persistenceReady: Promise<void>;
  private resolver: MultiFactorResolver | null = null;
  private enrollment: { secret: TotpSecret; user: User } | null = null;

  constructor(
    app: FirebaseApp,
    private readonly emulatorUrl?: string,
  ) {
    this.auth = getAuth(app);
    this.persistenceReady = setPersistence(this.auth, browserLocalPersistence);
    if (emulatorUrl && this.auth.emulatorConfig === null) {
      connectAuthEmulator(this.auth, emulatorUrl, { disableWarnings: true });
    }
  }

  async beginTotpEnrollment(): Promise<TotpEnrollment> {
    await this.persistenceReady;
    const user = this.auth.currentUser;
    if (!user) throw new Error("Sign in again before enrolling MFA.");
    if (this.emulatorUrl) {
      return {
        qrCodeUrl: `otpauth://totp/Cyra:${encodeURIComponent(user.email ?? "administrator")}?secret=JBSWY3DPEHPK3PXP&issuer=Cyra`,
        secretKey: "JBSW Y3DP EHPK 3PXP",
      };
    }
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    this.enrollment = { secret, user };
    return {
      qrCodeUrl: secret.generateQrCodeUrl(user.email ?? "administrator", "Cyra"),
      secretKey: secret.secretKey,
    };
  }

  async completeTotpEnrollment(code: string): Promise<void> {
    await this.persistenceReady;
    if (this.emulatorUrl) {
      await this.localMfaRequest("enroll", code);
      await this.auth.currentUser?.getIdToken(true);
      return;
    }
    if (!this.enrollment) throw new Error("The MFA enrollment session expired.");
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(this.enrollment.secret, code);
    await multiFactor(this.enrollment.user).enroll(assertion, "Cyra authenticator");
    this.enrollment = null;
  }

  async currentToken(forceRefresh = false): Promise<string | null> {
    await this.persistenceReady;
    await this.auth.authStateReady();
    return (await this.auth.currentUser?.getIdToken(forceRefresh)) ?? null;
  }

  async signIn(email: string, password: string): Promise<SignInOutcome> {
    await this.persistenceReady;
    this.resolver = null;
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      if (this.emulatorUrl) {
        const token = await credential.user.getIdTokenResult(true);
        const claims = token.claims as { cyra_local_mfa?: unknown };
        return { mfaRequired: claims.cyra_local_mfa === true };
      }
      return { mfaRequired: false };
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        error.code === "auth/multi-factor-auth-required"
      ) {
        this.resolver = getMultiFactorResolver(this.auth, error as MultiFactorError);
        return { mfaRequired: true };
      }
      throw error;
    }
  }

  async signInWithCustomToken(token: string): Promise<void> {
    await this.persistenceReady;
    this.resolver = null;
    this.enrollment = null;
    await signInWithCustomToken(this.auth, token);
  }

  async signOut(): Promise<void> {
    await this.persistenceReady;
    this.resolver = null;
    await signOut(this.auth);
  }

  async verifyTotp(code: string): Promise<void> {
    await this.persistenceReady;
    if (this.emulatorUrl) {
      await this.localMfaRequest("assert", code);
      return;
    }
    const resolver = this.resolver;
    const hint = resolver?.hints.find(
      (candidate) => candidate.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (!resolver || !hint) throw new Error("The MFA challenge expired. Sign in again.");
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
    await resolver.resolveSignIn(assertion);
    this.resolver = null;
  }

  private async localMfaRequest(action: "assert" | "enroll", code: string): Promise<void> {
    const token = await this.auth.currentUser?.getIdToken();
    if (!token) throw new Error("Sign in again before continuing local MFA.");
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000";
    const response = await fetch(`${apiBaseUrl.replace(/\/$/u, "")}/v1/local-auth/mfa/${action}`, {
      body: JSON.stringify({ code }),
      headers: {
        authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
      method: "POST",
    });
    if (!response.ok) {
      const result = (await response.json().catch(() => undefined)) as
        | { error?: { message?: string } }
        | undefined;
      throw new Error(result?.error?.message ?? "Local MFA could not be verified.");
    }
  }
}

function firebaseAdapter(): IdentityAdapter | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  if (!apiKey || !authDomain || !projectId || !appId) return null;
  const app =
    getApps()[0] ??
    initializeApp({
      apiKey,
      appId,
      authDomain,
      projectId,
    });
  return new FirebaseIdentityAdapter(app, firebaseAuthEmulatorUrl);
}

const configuredFirebaseAdapter = isDemoMode ? null : firebaseAdapter();

export const identityAccess = {
  available: isDemoMode || firebaseConfigurationReady,
  configurationMessage:
    !isDemoMode && !firebaseConfigurationReady
      ? "Set VITE_FIREBASE_API_KEY, VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID, and VITE_FIREBASE_APP_ID before API-mode sign-in."
      : null,
  proofHint: isDemoMode
    ? "Fictional local sign-in: use any email address and code 123456. No email is sent."
    : null,
} as const;

export const identityAdapter: IdentityAdapter = isDemoMode
  ? new DemoIdentityAdapter()
  : (configuredFirebaseAdapter ?? new MissingFirebaseConfigurationAdapter());
